// import { BASE_API_URL } from '@/constants/commonStrings.constant';
// import axios, { AxiosRequestConfig } from 'axios';
import { BASE_UAPI_URL } from '@/constants/commonStrings.constant';
import { apiService } from '../api.service';

type LogLevel = 'debug' | 'warn' | 'info' | 'error' | 'crit' | 'json';

type PostLogParams = {
  level: LogLevel;
  message: string;
}

// /**
//  * Service for sending logs to DataDog using apiService
//  * 
//  * Log Levels:
//  * - debug: Enables/Disables all debug level logs

//  * - warn: Enables/Disables all warning level logs  
//  * - info: Enables/Disables all Notice and Info level logs
//  * - error: Enables/Disables all Alert and Error level logs (recommended minimum)
//  * - crit: Enables/Disables all Critical level logs
//  */
// const LogService = () => {
//   /**
//    * Posts a log message to DataDog

//    * @param level - The log level ('debug'|'warn'|'info'|'error'|'crit')
//    * @param message - Detailed message describing the log context and relevant debug info
//    * @returns Promise with the response data or error
//    */

//   const postLog = async (
//     params: PostLogParams,
//     config?: Partial<AxiosRequestConfig>,
//   ): Promise<any> => {

//     const { level, message } = params;
//     const requestId = `postLog_${Date.now()}`;

//     const response = await apiService({
//       method: 'post',
//       url: `${BASE_API_URL}/logthis/${level}`,
//       ...config,
//       id: requestId,
//       data: { Message: message }
//     });

//     return response.data;
//   };


//   return {
//     debug: (message: string) => postLog({ level: 'debug', message }),
//     warn: (message: string) => postLog({ level: 'warn', message }), 
//     info: (message: string) => postLog({ level: 'info', message }),
//     error: (message: string) => postLog({ level: 'error', message }),
//     critical: (message: string) => postLog({ level: 'crit', message })
//   };


// };


// export default LogService;

// log.service.ts
type LogEntry = PostLogParams & {
  timestamp: number;
  retryCount: number;
  hash: string; // Add hash for deduplication
};

// Only batch up to 10 logs at a time to avoid overwhelming the API
const MAX_BATCH_SIZE = 10;

// Process logs more frequently (500ms) to improve responsiveness
const MAX_WAIT_TIME = 500;

// Maximum retries for failed log attempts
const MAX_RETRIES = 3;

// Only keep last 100 logs in queue to prevent memory issues
const MAX_QUEUE_SIZE = 100;

const createLogService = () => {
  let logQueue: LogEntry[] = [];
  let timeoutId: NodeJS.Timeout | null = null;
  let isSending = false;
  let recentLogs = new Set<string>(); // Track recent log hashes
  const DEDUP_WINDOW_MS = 1000; // 1 second deduplication window

  // Helper to create a hash from log content
  const createLogHash = (level: LogLevel, message: string, timestamp: number) => {
    return `${level}-${message}-${Math.floor(timestamp / DEDUP_WINDOW_MS)}`;
  };

  // Clean old entries from recentLogs
  const cleanRecentLogs = () => {
    const cutoffTime = Math.floor(Date.now() / DEDUP_WINDOW_MS);
    recentLogs.forEach(hash => {
      const [, , timeWindow] = hash.split('-');
      if (Number(timeWindow) < cutoffTime) {
        recentLogs.delete(hash);
      }
    });
  };

  const processQueue = async () => {
    if (isSending || logQueue.length === 0) return;

    isSending = true;
    const batch = logQueue.splice(0, MAX_BATCH_SIZE);

    try {
      // Group logs by level since we need separate requests for each level
      const logsByLevel = batch.reduce((acc, log) => {
        if (!acc[log.level]) {
          acc[log.level] = [];
        }
        acc[log.level].push(log);
        return acc;
      }, {} as Record<LogLevel, LogEntry[]>);

      // Send separate requests for each log level
      await Promise.all(
        Object.entries(logsByLevel).map(([level, logs]) =>
          apiService({
            method: 'post',
            url: `${BASE_UAPI_URL}/logthis/${level}`,
            data: {
              message: logs[0].message
            },
            options: {
              isLogRequest: true
            }
          })
        )
      );
    } catch (error) {
      // Handle failed logs - requeue with retry count
      const failedLogs = batch.map(log => ({
        ...log,
        retryCount: (log.retryCount || 0) + 1
      }));

      // Only requeue logs that haven't exceeded max retries
      const retriableLogs = failedLogs.filter(log => log.retryCount < MAX_RETRIES);
      if (retriableLogs.length > 0) {
        logQueue.unshift(...retriableLogs);
      }
    } finally {
      isSending = false;

      // If there are more logs, schedule next processing
      if (logQueue.length > 0) {
        timeoutId = setTimeout(processQueue, MAX_WAIT_TIME);
      }
    }
  };

  const queueLog = (params: PostLogParams) => {
    const timestamp = Date.now();
    const hash = createLogHash(params.level, params.message, timestamp);

    // Clean old entries periodically
    cleanRecentLogs();

    // Skip if we've seen this log recently
    if (recentLogs.has(hash)) {
      return;
    }

    // Add to recent logs
    recentLogs.add(hash);

    // Enforce queue size limit by removing oldest logs if necessary
    if (logQueue.length >= MAX_QUEUE_SIZE) {
      logQueue = logQueue.slice(-MAX_QUEUE_SIZE + 1);
    }

    // Add new log with retry count and hash
    logQueue.push({
      ...params,
      timestamp,
      retryCount: 0,
      hash
    });

    // Start processing if not already started
    if (logQueue.length >= MAX_BATCH_SIZE) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      processQueue();
    } else if (!timeoutId) {
      timeoutId = setTimeout(processQueue, MAX_WAIT_TIME);
    }
  };

  // Clean up function
  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    logQueue = [];
    isSending = false;
    recentLogs.clear(); // Clear the deduplication set
  };

  const postLog = async ({ level, message }: PostLogParams) => {
    try {
      await apiService({
        method: 'post',
        url: `${BASE_UAPI_URL}/logthis/${level}`,
        data: {
          message: message
        },
        options: {
          isLogRequest: true
        }
      });
    } catch (error) {
      console.error('Failed to send immediate log:', error);
      return false;
    }
  };

  const sendLog = async ({ level, message }: PostLogParams) => {
    try {
      await postLog({ level, message });
      return true;
    } catch (error) {
      console.error('Failed to send immediate log:', error);
      return false;
    }
  };

  return {
    debug: (message: string) => queueLog({ level: 'debug', message }),
    warn: (message: string) => queueLog({ level: 'warn', message }),
    info: (message: string) => queueLog({ level: 'info', message }),
    error: (message: string) => queueLog({ level: 'error', message }),
    critical: (message: string) => queueLog({ level: 'crit', message }),
    json: (message: string) => queueLog({ level: 'json', message }),
    // Send a log immediately without queueing
    sendLog,
    cleanup, 
    
  };
};

export const LogService = createLogService();

